import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Package, ExternalLink, CalendarDays } from "lucide-react";
import { relatorioEntregasRealizadas } from "../services/entregas";
import { listarUsuariosAtivos } from "../services/usuarios";
import { listarClientes } from "../services/clientes";
import { listarTiposTarefa } from "../services/tiposTarefa";
import { Avatar } from "../components/Avatar";

function primeiroDiaMes() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}
function hojeISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function diaBonito(dia: string) {
  // dia = "YYYY-MM-DD" (local) — monta sem new Date pra não escorregar de fuso.
  const [a, m, d] = dia.split("-").map(Number);
  const dt = new Date(a, m - 1, d);
  return dt.toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "2-digit" });
}

export function EntregasRealizadas() {
  const [inicio, setInicio] = useState(primeiroDiaMes());
  const [fim, setFim] = useState(hojeISO());
  const [usuarioId, setUsuarioId] = useState("");
  const [clienteId, setClienteId] = useState("");
  const [categoria, setCategoria] = useState("");

  const { data: usuarios = [] } = useQuery({ queryKey: ["usuarios-ativos"], queryFn: listarUsuariosAtivos });
  const { data: clientes = [] } = useQuery({ queryKey: ["clientes"], queryFn: listarClientes });
  const { data: tipos = [] } = useQuery({ queryKey: ["tipos-tarefa-todos"], queryFn: () => listarTiposTarefa({ todos: true }) });
  const categorias = useMemo(() => {
    const set = new Set<string>();
    for (const t of tipos as any[]) if (t.categoriaRelatorio) set.add(t.categoriaRelatorio);
    return Array.from(set).sort();
  }, [tipos]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["entregas-realizadas", inicio, fim, usuarioId, clienteId, categoria],
    queryFn: () => relatorioEntregasRealizadas({
      inicio: `${inicio}T00:00:00`,
      fim: `${fim}T23:59:59`,
      usuarioId: usuarioId || undefined,
      clienteId: clienteId || undefined,
      categoria: categoria || undefined,
    }),
    enabled: !!inicio && !!fim,
  });

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
          <Package size={20} /> Entregas realizadas
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          O que a equipe de fato entregou — por dia, pessoa, categoria e cliente. Cada entrega é registrada no card pelo botão <strong>Realizado</strong> (soma peças ao longo do tempo, independente da coluna Finalizado).
        </p>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-3 items-end bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
        <div>
          <label className="text-xs text-slate-500 block mb-1">De</label>
          <input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-sm outline-none" />
        </div>
        <div>
          <label className="text-xs text-slate-500 block mb-1">Até</label>
          <input type="date" value={fim} onChange={(e) => setFim(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-sm outline-none" />
        </div>
        <div>
          <label className="text-xs text-slate-500 block mb-1">Pessoa</label>
          <select value={usuarioId} onChange={(e) => setUsuarioId(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-sm outline-none">
            <option value="">Todas</option>
            {usuarios.map((u: any) => <option key={u.id} value={u.id}>{u.nome}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-slate-500 block mb-1">Cliente</label>
          <select value={clienteId} onChange={(e) => setClienteId(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-sm outline-none">
            <option value="">Todos</option>
            {clientes.map((c: any) => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-slate-500 block mb-1">Categoria</label>
          <select value={categoria} onChange={(e) => setCategoria(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-sm outline-none">
            <option value="">Todas</option>
            {categorias.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>

      {isLoading && <p className="text-slate-400 text-sm">Carregando…</p>}
      {isError && <p className="text-red-500 text-sm">Erro ao carregar.</p>}

      {data && (
        <>
          {/* Totais */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
              <p className="text-xs text-slate-400 mb-1">Total no período</p>
              <p className="text-3xl font-bold text-emerald-600">{data.totalPecas}</p>
              <p className="text-xs text-slate-500">{data.totalPecas === 1 ? "peça entregue" : "peças entregues"} · {data.totalRegistros} {data.totalRegistros === 1 ? "registro" : "registros"}</p>
            </div>
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
              <p className="text-xs text-slate-400 mb-2">Por pessoa</p>
              {data.porPessoa.length === 0 ? <p className="text-xs text-slate-400">—</p> : (
                <ul className="space-y-1.5">
                  {data.porPessoa.slice(0, 6).map((p) => (
                    <li key={p.usuarioId} className="flex items-center gap-2 text-sm">
                      <Avatar nome={p.usuarioNome} fotoUrl={p.usuarioFotoUrl} tamanho={20} />
                      <span className="flex-1 truncate text-slate-700 dark:text-slate-200">{p.usuarioNome}</span>
                      <span className="font-semibold tabular-nums text-slate-700 dark:text-slate-200">{p.totalPecas}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
              <p className="text-xs text-slate-400 mb-2">Por categoria / cliente</p>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {data.porCategoria.map((c) => (
                  <span key={c.categoria} className="text-[11px] px-1.5 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 font-medium">{c.categoria}: {c.totalPecas}</span>
                ))}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {data.porCliente.slice(0, 8).map((c) => (
                  <span key={c.clienteId || c.clienteNome} className="text-[11px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">{c.clienteNome}: {c.totalPecas}</span>
                ))}
              </div>
            </div>
          </div>

          {/* Por dia */}
          {data.porDia.length === 0 ? (
            /* Estado vazio EXPLICADO: esta tela depende do botão "Realizado" no
               card, e a Rec decidiu não usar esse registro manual. Sem essa
               explicação a tela parece quebrada — a Thainá abriu duas vezes
               achando que era erro. Aponta pra onde os números de verdade estão. */
            <div className="max-w-xl mx-auto text-center py-10">
              <Package size={32} className="mx-auto mb-3 text-slate-300 dark:text-slate-600" />
              <p className="text-slate-500 dark:text-slate-400 font-medium">Nenhuma entrega registrada no período.</p>
              <div className="mt-3 text-sm text-slate-500 dark:text-slate-400 text-left bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg p-4 space-y-2">
                <p>
                  Esta tela mostra <strong>só</strong> o que for lançado no botão <strong>Realizado</strong>, dentro de cada card.
                  É um registro manual, feito pela pessoa que entregou — e hoje a equipe não usa esse botão, então ela fica vazia.
                </p>
                <p>
                  <strong>Não é erro, e nenhum outro número depende dela.</strong> Para ver o que foi entregue, use:
                </p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>
                    <Link to="/contratado-entregue" className="text-blue-600 hover:underline">Contratado × entregue</Link>
                    {" "}— o que foi entregue no mês, contra o que está contratado
                  </li>
                  <li>
                    <Link to="/produtividade" className="text-blue-600 hover:underline">Produtividade</Link>
                    {" "}— peças entregues por pessoa e por tipo
                  </li>
                </ul>
                <p className="text-xs text-slate-400">
                  Esses dois contam <strong>card finalizado</strong>, que a equipe já move no dia a dia — por isso funcionam sem
                  ninguém precisar preencher nada a mais.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {data.porDia.map((dia) => (
                <div key={dia.dia} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CalendarDays size={15} className="text-slate-400" />
                    <p className="font-semibold text-slate-800 dark:text-slate-100 capitalize">{diaBonito(dia.dia)}</p>
                    <span className="ml-auto text-sm font-bold text-emerald-700 bg-emerald-100 dark:bg-emerald-900/40 dark:text-emerald-300 px-2 py-0.5 rounded-full">{dia.totalPecas} {dia.totalPecas === 1 ? "peça" : "peças"}</span>
                  </div>
                  <div className="divide-y divide-slate-100 dark:divide-slate-800">
                    {dia.entregas.map((e) => (
                      <Link
                        key={e.id}
                        to={`/tarefas?tarefa=${e.tarefaId}`}
                        className="flex items-center gap-2 py-1.5 px-2 -mx-2 rounded hover:bg-slate-50 dark:hover:bg-slate-800 group text-sm"
                      >
                        <span className="font-bold text-emerald-700 dark:text-emerald-400 tabular-nums shrink-0 w-8 text-right">{e.quantidade}×</span>
                        <Avatar nome={e.usuarioNome} fotoUrl={e.usuarioFotoUrl} tamanho={20} />
                        <span className="flex-1 truncate text-slate-700 dark:text-slate-200 group-hover:text-blue-600">
                          {e.tipoIcone && <span className="mr-1">{e.tipoIcone}</span>}
                          {e.titulo}
                          {e.observacao && <span className="text-slate-400 italic"> · {e.observacao}</span>}
                        </span>
                        <span className="hidden sm:inline text-[11px] text-slate-400 shrink-0">{e.categoria}{e.clienteNome ? ` · ${e.clienteNome}` : ""}</span>
                        <ExternalLink size={13} className="text-slate-300 group-hover:text-blue-600 shrink-0" />
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
